/**
 * "Offline — figures as of 14:32" (mobile M5). Shown in the installed apps
 * whenever a screen is answering from its last good copy; gone with the first
 * fresh answer. Coming back online refetches everything at once.
 */

import { useQueryClient } from "@tanstack/react-query";
import { WifiOff } from "lucide-react";
import * as React from "react";

import { getOfflineState, subscribeOffline } from "@/lib/offline";

function clock(ms: number): string {
  const d = new Date(ms);
  const sameDay = d.toDateString() === new Date().toDateString();
  const time = d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: false });
  return sameDay ? time : `${d.getDate()} ${d.toLocaleString("en-IN", { month: "short" })} ${time}`;
}

export function OfflineBanner() {
  const state = React.useSyncExternalStore(subscribeOffline, getOfflineState);
  const qc = useQueryClient();

  React.useEffect(() => {
    const back = () => void qc.invalidateQueries();
    window.addEventListener("online", back);
    return () => window.removeEventListener("online", back);
  }, [qc]);

  if (!state.offline) return null;
  return (
    <div
      role="status"
      className="sticky top-[calc(3.5rem+var(--inset-top))] z-20 flex items-center justify-center gap-2 border-b border-warn/30 bg-warn/[0.12] px-4 py-2 text-xs text-ink backdrop-blur-xl md:top-0"
    >
      <WifiOff className="h-3.5 w-3.5 text-warn" aria-hidden />
      Offline — figures as of {state.asOf ? clock(state.asOf) : "your last visit"}
    </div>
  );
}
